import { useEffect, useRef } from 'react';
import { formatUsd } from '../lib/format';

const DURATION_MS = 700;

/**
 * A dollar figure that counts smoothly to each new value instead of
 * jumping (instantly with reduced motion). The text is written straight to
 * the element on animation frames, so it doesn't re-render React.
 */
export function AnimatedUsd({
  value,
  className,
}: {
  value: number | null;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const shown = useRef<number | null>(value);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const from = shown.current;
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (value === null || from === null || reduced || from === value) {
      shown.current = value;
      el.textContent = formatUsd(value);
      return;
    }
    const start = performance.now();
    let frame = 0;
    const step = () => {
      const t = Math.min(1, (performance.now() - start) / DURATION_MS);
      const eased = 1 - (1 - t) ** 3;
      shown.current = from + (value - from) * eased;
      el.textContent = formatUsd(shown.current);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value]);

  return (
    <span ref={ref} className={className}>
      {formatUsd(value)}
    </span>
  );
}
